import { useCallback, useMemo, useRef, useState } from 'react';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import styles from './MarketHealth.module.css';
import { ZONE_COLORS, riskScoreColor } from './riskScale';
import { parseDateMs } from './chartDateUtils';
import { MODE_HISTORY_FIELD, collectPositionTransitions } from './strategyTransitions';
import { selectLag } from './strategyLagSelect';
import { STRATEGY_CONFIGS, getStrategyScore } from './strategyConfigs';
import {
  baseChartOptions,
  baseXAxis,
  baseYAxis,
  getMarketHealthRangeSelector,
  getMarketHealthResponsiveRules,
  WEEKLY_DATA_GROUPING,
} from './chartTheme';

const isFiniteNumber = (v) => typeof v === 'number' && Number.isFinite(v);

const buildSortedPoints = (history, mode) =>
  (history || [])
    .filter((d) => d && typeof d.date === 'string' && !Number.isNaN(parseDateMs(d.date)))
    .map((d) => {
      const raw = getStrategyScore(d, mode);
      return {
        ...d,
        ts: parseDateMs(d.date),
        modeScore: isFiniteNumber(raw) ? raw : null,
      };
    })
    .sort((a, b) => a.ts - b.ts);

const SPARSE_HISTORY_THRESHOLD = 30;

const SPY_LINE_COLOR = '#6c757d';

const SPY_DATA_GROUPING = {
  ...WEEKLY_DATA_GROUPING,
  groupPixelWidth: 4,
  units: [
    ['day', [1]],
    ['week', [1]],
  ],
};

const TRADE_MARKER = {
  buy: { symbol: 'triangle', color: ZONE_COLORS.green },
  sell: { symbol: 'triangle-down', color: ZONE_COLORS.red },
};

const ZONE_BAND_COLORS = {
  green: 'rgba(40, 167, 69, 0.18)',
  red: 'rgba(220, 53, 69, 0.18)',
};

const LAYOUTS = [
  { key: 'spy', label: 'S&P 500' },
  { key: 'both', label: 'Both' },
  { key: 'score', label: 'Score' },
];

const TOTAL_PLOT_HEIGHT = { desktop: 520, mobile: 420, landscape: 300 };

const spyYAxis = () => ({
  ...baseYAxis,
  title: { ...baseYAxis.title, text: 'S&P 500' },
});

const scoreYAxis = (config) => {
  const [min, max] = config.scoreDomain;
  const boundary = config.riskBoundary;
  const showBoundaryLabel = config.scoreDomain[1] > 5;
  return {
    ...baseYAxis,
    min,
    max,
    tickLength: 0,
    tickWidth: 0,
    labels: { ...baseYAxis.labels, enabled: false },
    gridLineWidth: 0,
    title: { ...baseYAxis.title, text: 'Risk score' },
    plotBands: [
      { from: min, to: boundary, color: ZONE_BAND_COLORS.green },
      { from: boundary, to: max, color: ZONE_BAND_COLORS.red },
    ],
    plotLines: [
      {
        value: boundary,
        color: ZONE_COLORS.red,
        width: 1,
        dashStyle: 'Dash',
        zIndex: 3,
        ...(showBoundaryLabel
          ? {
            label: {
              text: config.riskBoundaryLabel,
              align: 'right',
              style: { color: '#6c757d', fontSize: '0.7rem' },
            },
          }
          : {}),
      },
    ],
  };
};

// Collapses a sorted list of points into contiguous x-axis time bands based on
// the selected strategy's position (invested=green, cash=red). Used by the
// single-pane SPY layout where xAxis plotBands are safe (only one pane).
const buildPositionBands = (points, modeField, lag) => {
  const bands = [];
  let run = null;

  const flush = () => {
    if (run) {
      const color = run.position === 'invested' ? ZONE_BAND_COLORS.green : ZONE_BAND_COLORS.red;
      bands.push({ from: run.from, to: run.to, color });
    }
    run = null;
  };

  const list = points || [];
  list.forEach((p, i) => {
    const position = selectLag(p?.[modeField], lag)?.position;
    const next = list[i + 1];
    const boundary = next && typeof next.ts === 'number' ? next.ts : p.ts;
    if (!position) {
      flush();
      return;
    }
    if (run && run.position === position) {
      run.to = boundary;
    } else {
      flush();
      run = { position, from: p.ts, to: boundary };
    }
  });
  flush();

  return bands;
};

// Converts position transitions into Highcharts zones format (zoneAxis: 'x').
// Each zone paints the x-range for one contiguous position run in the palette's
// green (invested) / red (cash) color. Used by the "Both" layout to color the
// S&P line itself by strategy position — solid ZONE_COLORS for the line — where
// xAxis plotBands can't be confined to one pane.
const buildColorCodeZones = (points, modeField, lag, palette) => {
  if (points.length === 0 || !modeField) return undefined;
  const colorFor = (position) => (position === 'invested' ? palette.green : palette.red);
  const zones = [];
  let currentPosition = selectLag(points[0]?.[modeField], lag)?.position;
  for (let i = 1; i < points.length; i++) {
    const position = selectLag(points[i]?.[modeField], lag)?.position;
    if (position && position !== currentPosition) {
      zones.push({ value: points[i].ts - 1, color: colorFor(currentPosition) });
      currentPosition = position;
    }
  }
  zones.push({ color: colorFor(currentPosition) });
  return zones;
};

// Builds the S&P 500 price chart options for the single-pane "S&P 500" layout.
const buildSpyOptions = (
  sorted,
  { heights, withRangeSelector, withNavigator, onSetExtremes, colorCodeSpy, mode, lag }
) => {
  const spyPoints = sorted.filter((d) => typeof d.spy === 'number');
  const spyByTs = new Map(spyPoints.map((d) => [d.ts, d]));

  const modeField = MODE_HISTORY_FIELD[mode];
  const xPlotBands = colorCodeSpy && modeField
    ? buildPositionBands(spyPoints, modeField, lag)
    : undefined;

  const buildTradeMarkerSeries = () => {
    if (!modeField) return [];
    const transitions = collectPositionTransitions(spyPoints, modeField, lag);
    if (transitions.length === 0) return [];

    const toSeries = (type) => ({
      type: 'scatter',
      name: type === 'buy' ? 'Re-entry' : 'Exit',
      color: TRADE_MARKER[type].color,
      marker: { enabled: true, symbol: TRADE_MARKER[type].symbol, radius: 5 },
      dataGrouping: { enabled: false },
      enableMouseTracking: true,
      data: transitions
        .filter((t) => t.type === type && spyByTs.has(t.ts))
        .map((t) => ({ x: t.ts, y: spyByTs.get(t.ts).spy })),
    });

    return [toSeries('buy'), toSeries('sell')].filter((s) => s.data.length > 0);
  };

  return {
    ...baseChartOptions,
    chart: { ...baseChartOptions.chart, height: heights.desktop },
    responsive: getMarketHealthResponsiveRules(
      heights.mobile,
      heights.landscape,
      withRangeSelector
    ),
    rangeSelector: withRangeSelector ? getMarketHealthRangeSelector() : { enabled: false },
    navigator: { enabled: withNavigator },
    scrollbar: { enabled: false },
    xAxis: {
      ...baseXAxis,
      crosshair: true,
      plotBands: xPlotBands,
      events: { setExtremes: onSetExtremes },
    },
    yAxis: [spyYAxis()],
    tooltip: {
      ...baseChartOptions.tooltip,
      formatter() {
        const ts = this.point?.x ?? this.x;
        const point = spyByTs.get(ts);
        if (!point) return '';
        const seriesName = this.series?.name;
        if (seriesName === 'Re-entry' || seriesName === 'Exit') {
          const action = seriesName === 'Re-entry' ? 'Buy' : 'Sell';
          return `<b>${point.date}</b><br/>S&P 500: ${point.spy}<br/>${action}`;
        }
        const pos = modeField ? selectLag(point[modeField], lag)?.position : null;
        const posLabel = pos === 'invested' ? 'Invested' : pos === 'cash' ? 'Cash' : '—';
        return `<b>${point.date}</b><br/>S&P 500: ${point.spy}<br/>Position: ${posLabel}`;
      },
    },
    plotOptions: {
      series: { dataGrouping: SPY_DATA_GROUPING },
    },
    series: [
      {
        name: 'S&P 500',
        yAxis: 0,
        color: SPY_LINE_COLOR,
        lineWidth: 2.5,
        data: spyPoints.map((d) => [d.ts, d.spy]),
        marker: { enabled: spyPoints.length < 30 },
      },
      ...buildTradeMarkerSeries(),
    ],
  };
};

// Builds the score chart options for the single-pane "Score" layout.
const buildScoreOptions = (
  sorted,
  currentScore,
  { heights, withRangeSelector, withNavigator, onSetExtremes, config }
) => {
  const boundary = config.riskBoundary;
  const scoreLineColor = riskScoreColor(currentScore, boundary);
  const rowByTs = new Map(sorted.map((d) => [d.ts, d]));
  const isIntegerScore = config.scoreDomain[1] <= 5;

  return {
    ...baseChartOptions,
    chart: { ...baseChartOptions.chart, height: heights.desktop },
    responsive: getMarketHealthResponsiveRules(
      heights.mobile,
      heights.landscape,
      withRangeSelector
    ),
    rangeSelector: withRangeSelector ? getMarketHealthRangeSelector() : { enabled: false },
    navigator: { enabled: withNavigator },
    scrollbar: { enabled: false },
    xAxis: {
      ...baseXAxis,
      crosshair: true,
      events: { setExtremes: onSetExtremes },
    },
    yAxis: [scoreYAxis(config)],
    tooltip: {
      ...baseChartOptions.tooltip,
      formatter() {
        const ts = this.point?.x ?? this.x;
        const point = rowByTs.get(ts);
        if (!point) return '';
        const scoreText = isFiniteNumber(point.modeScore)
          ? (isIntegerScore ? String(point.modeScore) : point.modeScore.toFixed(2))
          : '—';
        return `<b>${point.date}</b><br/>Risk score: ${scoreText}<br/>Risk threshold: ${boundary}`;
      },
    },
    plotOptions: {
      series: { dataGrouping: { enabled: false } },
    },
    series: [
      {
        name: 'Risk score',
        yAxis: 0,
        color: scoreLineColor,
        connectNulls: false,
        data: sorted.map((d) => [d.ts, isFiniteNumber(d.modeScore) ? d.modeScore : null]),
        marker: { enabled: sorted.length < 30 },
      },
    ],
  };
};

/**
 * Builds a single Highstock chart with two yAxis panes (SPY on top, Risk score
 * below) for the "Both" layout. A shared xAxis gives a unified crosshair,
 * range selector, and navigator — no cross-chart sync plumbing needed.
 *
 * Color-coding recolors the S&P LINE itself (zoneAxis: 'x' with solid green/
 * red ZONE_COLORS) rather than shading a band. xAxis plotBands are avoided
 * because they span all panes and would bleed into the score pane; the line
 * zones stay confined to the SPY line on yAxis 0.
 */
const buildBothOptions = (
  sorted,
  currentScore,
  { onSetExtremes, colorCodeSpy, mode, lag, config }
) => {
  const spyPoints = sorted.filter((d) => typeof d.spy === 'number');
  const spyByTs = new Map(spyPoints.map((d) => [d.ts, d]));
  const rowByTs = new Map(sorted.map((d) => [d.ts, d]));
  const modeField = MODE_HISTORY_FIELD[mode];
  const boundary = config.riskBoundary;
  const scoreLineColor = riskScoreColor(currentScore, boundary);
  const isIntegerScore = config.scoreDomain[1] <= 5;

  // Color the S&P line itself by strategy position (green=invested, red=cash)
  // via zoneAxis:'x' with solid ZONE_COLORS, instead of a faint shaded band.
  // Only in "Both" mode; the S&P-only layout keeps its xAxis plotBands.
  const spyZones = colorCodeSpy && modeField
    ? buildColorCodeZones(spyPoints, modeField, lag, ZONE_COLORS)
    : undefined;

  const tradeMarkerSeries = (() => {
    if (!modeField) return [];
    const transitions = collectPositionTransitions(spyPoints, modeField, lag);
    if (transitions.length === 0) return [];
    const toSeries = (type) => ({
      type: 'scatter',
      name: type === 'buy' ? 'Re-entry' : 'Exit',
      yAxis: 0,
      color: TRADE_MARKER[type].color,
      marker: { enabled: true, symbol: TRADE_MARKER[type].symbol, radius: 5 },
      dataGrouping: { enabled: false },
      enableMouseTracking: true,
      data: transitions
        .filter((t) => t.type === type && spyByTs.has(t.ts))
        .map((t) => ({ x: t.ts, y: spyByTs.get(t.ts).spy })),
    });
    return [toSeries('buy'), toSeries('sell')].filter((s) => s.data.length > 0);
  })();

  const riskScoreSeries = {
    name: 'Risk score',
    yAxis: 1,
    color: scoreLineColor,
    connectNulls: false,
    dataGrouping: { enabled: false },
    data: sorted.map((d) => [d.ts, isFiniteNumber(d.modeScore) ? d.modeScore : null]),
    marker: { enabled: sorted.length < 30 },
  };

  const allSeries = [
    {
      name: 'S&P 500',
      yAxis: 0,
      color: SPY_LINE_COLOR,
      lineWidth: 2.5,
      // Keep the S&P line ungrouped in Both mode so it stays on the same daily
      // x-grid as the ungrouped trade triangles (and, when on, the color
      // x-zones). With weekly grouping the line's points land on week buckets
      // while the triangles sit at exact daily timestamps, so the markers drift
      // off the line and jump as the time-window slider regroups. Ungrouped =
      // line, zones, and triangles all align at every zoom level.
      dataGrouping: { enabled: false },
      ...(spyZones ? { zoneAxis: 'x', zones: spyZones } : {}),
      data: spyPoints.map((d) => [d.ts, d.spy]),
      marker: { enabled: spyPoints.length < 30 },
    },
    ...tradeMarkerSeries,
    riskScoreSeries,
  ];

  const riskScoreIndex = allSeries.indexOf(riskScoreSeries);

  return {
    ...baseChartOptions,
    chart: { ...baseChartOptions.chart, height: TOTAL_PLOT_HEIGHT.desktop },
    // In the "Both" layout the SPY and Risk score lines share one chart, so
    // hovering either would put the other into Highcharts' default `inactive`
    // state (dimmed/blurred). The two panes are meant to be read together, so
    // keep every series at full opacity on hover — neither should fade.
    plotOptions: {
      ...baseChartOptions.plotOptions,
      series: {
        ...baseChartOptions.plotOptions?.series,
        states: { inactive: { opacity: 1 } },
      },
    },
    responsive: getMarketHealthResponsiveRules(
      TOTAL_PLOT_HEIGHT.mobile,
      TOTAL_PLOT_HEIGHT.landscape,
      true,
    ),
    rangeSelector: getMarketHealthRangeSelector(),
    navigator: { enabled: true, baseSeries: riskScoreIndex },
    scrollbar: { enabled: false },
    xAxis: {
      ...baseXAxis,
      crosshair: true,
      events: { setExtremes: onSetExtremes },
    },
    yAxis: [
      { ...spyYAxis(), height: '60%' },
      { ...scoreYAxis(config), top: '65%', height: '35%', offset: 0 },
    ],
    tooltip: {
      ...baseChartOptions.tooltip,
      formatter() {
        const ts = this.point?.x ?? this.x;
        const seriesName = this.series?.name;

        if (seriesName === 'Re-entry' || seriesName === 'Exit') {
          const point = spyByTs.get(ts);
          if (!point) return '';
          const action = seriesName === 'Re-entry' ? 'Buy' : 'Sell';
          return `<b>${point.date}</b><br/>S&P 500: ${point.spy}<br/>${action}`;
        }

        if (seriesName === 'S&P 500') {
          const point = spyByTs.get(ts);
          if (!point) return '';
          const pos = modeField ? selectLag(point[modeField], lag)?.position : null;
          const posLabel = pos === 'invested' ? 'Invested' : pos === 'cash' ? 'Cash' : '—';
          return `<b>${point.date}</b><br/>S&P 500: ${point.spy}<br/>Position: ${posLabel}`;
        }

        if (seriesName === 'Risk score') {
          const point = rowByTs.get(ts);
          if (!point) return '';
          const scoreText = isFiniteNumber(point.modeScore)
            ? (isIntegerScore ? String(point.modeScore) : point.modeScore.toFixed(2))
            : '—';
          return `<b>${point.date}</b><br/>Risk score: ${scoreText}<br/>Risk threshold: ${boundary}`;
        }

        return '';
      },
    },
    series: allSeries,
  };
};

/**
 * Score history chart. A 3-way pane-layout toggle ("S&P 500" / "Both" /
 * "Score") chooses which pane(s) to show:
 *   - 'spy' / 'score': a single full-height chart.
 *   - 'both': a SINGLE Highstock chart with two yAxis panes (SPY on top,
 *     score below), sharing one xAxis for a unified crosshair, range
 *     selector, and navigator.
 *
 * A "Color-code S&P" toggle (only meaningful when the S&P pane is visible)
 * colors the price green/red by the strategy's position. In "Both" mode this
 * recolors the S&P line itself (zoneAxis: 'x' line zones, confined to the SPY
 * pane) instead of xAxis plotBands (which would bleed into the score pane).
 *
 * The card height is constant across all three layouts (TOTAL_PLOT_HEIGHT),
 * so toggling never reflows the page.
 */
const ScoreChart = ({ history = [], score, mode = 'adaptiveBalanced', lag = false }) => {
  const config = STRATEGY_CONFIGS[mode] ?? STRATEGY_CONFIGS.adaptiveBalanced;
  const sorted = useMemo(
    () => buildSortedPoints(history, mode),
    [history, mode],
  );
  const [layout, setLayout] = useState('both');
  const [colorCodeSpy, setColorCodeSpy] = useState(false);

  const windowRef = useRef({ min: undefined, max: undefined });

  const recordWindow = useCallback(function onSetExtremes(e) {
    windowRef.current = { min: e.min, max: e.max };
  }, []);

  const chartCallback = useCallback((chart) => {
    if (windowRef.current.min === undefined && windowRef.current.max === undefined) {
      const { min, max } = chart.xAxis[0].getExtremes();
      if (min != null && max != null) {
        windowRef.current = { min, max };
      }
      return;
    }
    chart.xAxis[0].setExtremes(
      windowRef.current.min,
      windowRef.current.max,
      true,
      false,
    );
  }, []);

  if (sorted.length === 0) {
    return (
      <p className={styles.statusMessage}>
        Score history begins once the first daily score is computed. Check back after
        the next daily run.
      </p>
    );
  }

  const spyVisible = layout === 'spy' || layout === 'both';

  const chartOptions = layout === 'both'
    ? buildBothOptions(sorted, score, {
        onSetExtremes: recordWindow,
        colorCodeSpy,
        mode,
        lag,
        config,
      })
    : layout === 'spy'
      ? buildSpyOptions(sorted, {
          heights: TOTAL_PLOT_HEIGHT,
          withRangeSelector: true,
          withNavigator: true,
          onSetExtremes: recordWindow,
          colorCodeSpy,
          mode,
          lag,
        })
      : buildScoreOptions(sorted, score, {
          heights: TOTAL_PLOT_HEIGHT,
          withRangeSelector: true,
          withNavigator: true,
          onSetExtremes: recordWindow,
          config,
        });

  return (
    <div>
      <div className={styles.chartControls}>
        <div
          className={styles.viewToggle}
          role="group"
          aria-label="Score history panes"
        >
          {LAYOUTS.map(({ key, label }) => {
            const active = layout === key;
            return (
              <button
                key={key}
                type="button"
                className={active ? styles.viewToggleButtonActive : styles.viewToggleButton}
                aria-pressed={active}
                onClick={() => setLayout(key)}
              >
                {label}
              </button>
            );
          })}
        </div>

        <label className={styles.lagToggle}>
          <input
            type="checkbox"
            className={styles.lagToggleInput}
            checked={colorCodeSpy}
            disabled={!spyVisible}
            onChange={(e) => setColorCodeSpy(e.target.checked)}
            aria-label="Color-code S&P 500 background by market health zone"
          />
          <span className={styles.lagToggleTrack} aria-hidden="true" />
          <span className={styles.lagToggleLabel}>Color-code S&amp;P</span>
        </label>
      </div>

      <HighchartsReact
        // Remount when Color-code toggles. It flips the S&P line's zoneAxis/
        // zones and its dataGrouping (grouped ↔ ungrouped) at once; letting
        // HighchartsReact reconcile those in place on a persisted chart has
        // proven fragile (stale zones, misgrouped points). A clean remount
        // rebuilds the series from scratch; the selected time window survives
        // via windowRef + chartCallback.
        key={`${layout}-${colorCodeSpy ? 'cc' : 'plain'}`}
        highcharts={Highcharts}
        constructorType="stockChart"
        options={chartOptions}
        callback={chartCallback}
      />

      {sorted.length < SPARSE_HISTORY_THRESHOLD && (
        <p className={styles.sparseHistoryNote}>
          Score history begins {sorted[0].date}. More history accumulates daily.
        </p>
      )}
    </div>
  );
};

export default ScoreChart;
